'use client';

import { useState, useEffect } from "react";
import BookDemoButton from "./BookDemoButton";

/**
 * Top nav for the standalone DXR site. Full link row on desktop; at ≤768px the
 * links + CTAs collapse into a hamburger menu so the bar never crowds and every
 * link (Features, FAQ, Sign in, Try now, Book a demo) stays reachable on mobile.
 */
export default function SiteNav() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onResize = () => { if (window.innerWidth > 768) setOpen(false); };
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <header className="snav">
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <a className="snav-logo" href="/" aria-label="NATOE DXR">
        <span className="snav-product">NATOE DXR</span>
      </a>

      {/* Desktop links */}
      <nav className="snav-links">
        <a href="#features">Features</a>
        <a href="#faq">FAQ</a>
        <a href="/login">Sign in</a>
        <a className="snav-cta" href="/dashboard?trial=1">Try now →</a>
        <BookDemoButton className="snav-cta snav-ghost" />
      </nav>

      {/* Mobile hamburger */}
      <button
        type="button"
        className="snav-burger"
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        aria-controls="snav-mobile"
        onClick={() => setOpen((o) => !o)}
      >
        <span /><span /><span />
      </button>

      {/* Mobile dropdown panel */}
      <nav id="snav-mobile" className={`snav-mobile ${open ? "is-open" : ""}`} aria-label="Mobile menu">
        <a href="#features" onClick={close}>Features</a>
        <a href="#faq" onClick={close}>FAQ</a>
        <a href="/login" onClick={close}>Sign in</a>
        <a className="snav-mcta" href="/dashboard?trial=1" onClick={close}>Try now →</a>
        <BookDemoButton className="snav-mcta snav-mghost" />
      </nav>
    </header>
  );
}

const styles = `
.snav { position: sticky; top: 0; z-index: 50; display: flex; align-items: center; justify-content: space-between; padding: 16px 5vw; background: rgba(5,7,13,0.72); backdrop-filter: blur(12px); border-bottom: 1px solid; border-image-source: linear-gradient(90.69deg, rgba(240,243,253,0) -8.63%, rgba(240,243,253,0.6) 50.57%, rgba(255,255,255,0) 109.76%); border-image-slice: 1; font-family: Inter, sans-serif; }
.snav-logo { display: inline-flex; align-items: center; gap: 14px; text-decoration: none; white-space: nowrap; }
/* "NATOE DXR" wordmark — geometric Poppins, softened white. */
.snav-product { font-family: 'Poppins', sans-serif; font-size: 32px; font-weight: 400; letter-spacing: 0.03em; line-height: 1; color: rgba(255,255,255,0.9); }

.snav-links { display: flex; align-items: center; gap: clamp(16px, 3vw, 32px); }
.snav-links a { font-size: 15px; font-weight: 500; color: rgba(255,255,255,0.75); text-decoration: none; }
.snav-links a:hover { color: #fff; }
.snav-cta { background: #2997FF; color: #00121f !important; font-weight: 600 !important; padding: 9px 20px; border-radius: 999px; border: 1px solid #2997FF; white-space: nowrap; transition: background 200ms, color 200ms; }
.snav-cta:hover { background: #1f86e8; border-color: #1f86e8; }
.snav-cta.snav-ghost { background: transparent; color: #2997FF !important; border: 1px solid rgba(41,151,255,0.5); }
.snav-cta.snav-ghost:hover { background: rgba(41,151,255,0.12); border-color: #2997FF; }

/* Hamburger — shown only on mobile */
.snav-burger { display: none; width: 42px; height: 42px; flex-direction: column; align-items: center; justify-content: center; gap: 5px; background: transparent; border: 0; cursor: pointer; padding: 0; }
.snav-burger span { display: block; width: 23px; height: 2px; border-radius: 2px; background: #fff; transition: transform 220ms ease, opacity 160ms ease; }
.snav-burger[aria-expanded="true"] span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.snav-burger[aria-expanded="true"] span:nth-child(2) { opacity: 0; }
.snav-burger[aria-expanded="true"] span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* Mobile dropdown panel */
.snav-mobile { display: none; position: absolute; top: 100%; left: 0; right: 0; flex-direction: column; padding: 8px 5vw 20px; background: rgba(5,7,13,0.97); backdrop-filter: blur(14px); border-bottom: 1px solid rgba(255,255,255,0.1); box-shadow: 0 22px 44px -18px rgba(0,0,0,0.65); }
.snav-mobile.is-open { display: flex; }
.snav-mobile a { font-size: 16px; font-weight: 500; color: rgba(255,255,255,0.85); text-decoration: none; padding: 13px 4px; border-bottom: 1px solid rgba(255,255,255,0.07); transition: color 150ms; }
.snav-mobile a:hover { color: #fff; }
.snav-mobile a.snav-mcta { margin-top: 12px; padding: 13px 20px; text-align: center; border-radius: 999px; font-weight: 600; background: #2997FF; color: #00121f; border: 1px solid #2997FF; }
.snav-mobile a.snav-mcta.snav-mghost { margin-top: 10px; background: transparent; color: #2997FF; border: 1px solid rgba(41,151,255,0.5); }

@media (max-width: 768px) {
  .snav-links { display: none; }
  .snav-burger { display: flex; }
}
/* The dropdown is mobile-only; never let a stale open state show it on desktop. */
@media (min-width: 769px) {
  .snav-mobile { display: none !important; }
}
@media (max-width: 560px) {
  .snav { padding: 12px 5vw; }
  .snav-product { font-size: 26px; }
}
@media (max-width: 400px) {
  .snav-logo { gap: 9px; }
  .snav-product { font-size: 21px; }
}
`;
